import { useCallback, useEffect, useRef, useState } from "react";

const SEEN_KEY = "arr_listing_views_v1";
const DONE_KEY = "arr_listing_gate_submitted_v1";
const NEXT_KEY = "arr_listing_gate_next_v1";
export const GATE_EVERY = 3;

const memory: Record<string, string> = {};
const read = (k: string): string | null => {
  try {
    const v = window.localStorage.getItem(k);
    if (v !== null) return v;
  } catch {
    /* storage unavailable */
  }
  return memory[k] ?? null;
};
const write = (k: string, v: string) => {
  memory[k] = v;
  try {
    window.localStorage.setItem(k, v);
  } catch {
    /* storage unavailable */
  }
};

/** Single-listing detail pages (IDX Broker) — e.g. /idx/details/listing/... */
export const isListingDetailsUrl = (href: string): boolean => {
  try {
    const u = new URL(href, window.location.href);
    return /\/idx\/details\//i.test(u.pathname);
  } catch {
    return false;
  }
};

/** Stable dedupe key: path without trailing slash, query or hash. */
export const listingKey = (href: string): string => {
  const u = new URL(href, window.location.href);
  return (u.host + u.pathname).replace(/\/+$/, "").toLowerCase();
};

/**
 * Site-wide listing-click gate. Counts distinct listing detail clicks
 * (our cards + IDX widget shadow DOM via composedPath) and asks to open
 * the lead form on every 3rd new listing until the visitor submits.
 */
export const useListingViewGate = () => {
  const [open, setOpen] = useState(false);
  const pendingUrl = useRef<string | null>(null);

  const onClick = useCallback((e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (read(DONE_KEY) === "1") return;
    const path = e.composedPath();
    const anchor = path.find(
      (n): n is HTMLAnchorElement => n instanceof HTMLAnchorElement && !!n.href,
    );
    if (!anchor || !isListingDetailsUrl(anchor.href)) return;

    let seen: string[] = [];
    try {
      seen = JSON.parse(read(SEEN_KEY) || "[]");
      if (!Array.isArray(seen)) seen = [];
    } catch {
      seen = [];
    }
    const key = listingKey(anchor.href);
    if (seen.includes(key)) return;
    seen.push(key);
    write(SEEN_KEY, JSON.stringify(seen));

    const next = Number(read(NEXT_KEY)) || GATE_EVERY;
    if (seen.length < next) return;

    e.preventDefault();
    e.stopPropagation();
    write(NEXT_KEY, String(seen.length + GATE_EVERY));
    pendingUrl.current = anchor.href;
    setOpen(true);
  }, []);

  useEffect(() => {
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [onClick]);

  const openPending = () => {
    const url = pendingUrl.current;
    pendingUrl.current = null;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const close = useCallback(() => {
    setOpen(false);
    openPending();
  }, []);

  const submitted = useCallback(() => {
    write(DONE_KEY, "1");
    setOpen(false);
    openPending();
  }, []);

  return { open, close, submitted };
};
